import type { ChatClient, ChatRoomSummary } from '@wtfalch/chat';
import { Empty, Panes } from '@wtfalch/design';
import { useCallback, useEffect, useState } from 'react';

import type { RoomCallBinding } from '../calls/roomCalls.ts';
import type { ColleagueHoursSource } from '../hours/ColleagueHours.tsx';
import type { ChatLocation } from '../routes.ts';
import { ChatList } from './ChatList.tsx';
import { Encryption, useIncomingVerification } from './Encryption.tsx';
import { useEncryptedRoomIds } from './encryption.ts';
import type { ListView } from './format.ts';
import { InfoPanel, type InfoPanelTab } from './InfoPanel.tsx';
import { Room } from './Room.tsx';
import { ThreadPanel } from './ThreadPanel.tsx';
import './chat.css';

const HOME: ChatLocation = { roomId: null, threadId: null, eventId: null };

/**
 * The client's own room list: started when this section mounts, never
 * stopped here -- `onRoomsChanged` is the client's own push straight off
 * matrix-js-sdk's sync, so nothing here polls. `client.start()` is
 * idempotent (a no-op once started, or joins a start already in flight --
 * see `ChatClient.start`), so a Chat section that mounts, unmounts and
 * mounts again just rejoins the same running session. `stop()` belongs to
 * `AgoraView` (`App.tsx`) alone: it created this client and shares it with
 * the header search, the idleness watcher and Today, none of which know
 * this section exists, so this section leaving must not end their session
 * out from under them. Own rewrite of the same shape `@wtfalch/chat`'s own
 * (unused here) `Chat` component uses for this -- see this file's own
 * docblock for why Agora draws this section itself instead.
 */
function useChatRooms(client: ChatClient) {
  const [rooms, setRooms] = useState<ChatRoomSummary[]>(() => client.rooms());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` only re-runs the start after a failure ("Try again").
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    setRooms(client.rooms());
    const unsubscribe = client.onRoomsChanged((next) => {
      if (live) setRooms(next);
    });
    client.start().then(
      () => {
        if (!live) return;
        setRooms(client.rooms());
        setLoading(false);
      },
      (reason: unknown) => {
        if (!live) return;
        setLoading(false);
        setError(reason instanceof Error ? reason : new Error('The chat session did not start.'));
      },
    );
    return () => {
      live = false;
      unsubscribe();
    };
  }, [client, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { rooms, loading, error, retry };
}

/**
 * The Chat section: the room list, a selected room's pane, and -- in the
 * third column -- an open thread or the room's own info. Matches the design
 * canvas's `Chat.dc.html` (a channel, its thread panel open) and
 * `Chat-dm.dc.html` (a direct message, its info panel open) on
 * `@wtfalch/chat`'s `ChatClient` alone.
 *
 * `location`/`onNavigate` follow `Mail`'s own contract: the address is the
 * state. `infoOpen` and the room list's `view` are not -- a display
 * preference and a panel's own open/closed are not places the agora's URL
 * needs to name, the same reason Mail's `composing` and sort are local too.
 */
export function Chat({
  client,
  location,
  onNavigate,
  calls,
  hours,
  linkFor,
  encryption = false,
  initialDialog,
}: {
  client: ChatClient;
  location: ChatLocation;
  onNavigate: (location: ChatLocation) => void;
  /** Threaded straight through to `Room` -- see its own docblock for why
   *  this is optional. */
  calls?: RoomCallBinding;
  /** Working hours for the info panel's member rows; absent when this
   *  company has none (agora#258). */
  hours?: ColleagueHoursSource;
  /** The address of a place in a room, for a message's "Copy link". */
  linkFor?: (location: ChatLocation) => string;
  /** Whether this company allows encrypted channels (agora#135): the lock
   *  button, the badges and the verification prompt are drawn only then. */
  encryption?: boolean;
  /** A list dialog to open on mount; the preview states use it. */
  initialDialog?: 'new' | 'browse';
}) {
  const { rooms, loading, error, retry } = useChatRooms(client);
  const [view, setView] = useState<ListView>('all');
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoTab, setInfoTab] = useState<InfoPanelTab>('room');
  const [encryptionOpen, setEncryptionOpen] = useState(false);
  const incoming = useIncomingVerification(client, encryption);
  const encryptedIds = useEncryptedRoomIds(client, rooms, encryption);

  const selected = rooms.find((r) => r.id === location.roomId && r.membership !== 'invite');

  // Nobody picked a room yet: land in the first one there is, the way
  // Mail lands in the Inbox -- a person opening Chat wants to read
  // something, not choose a starting point every time. Not on a phone,
  // where the list is the page and "Back to rooms" would otherwise
  // bounce straight back into the first room.
  useEffect(() => {
    if (loading || location.roomId) return;
    if (window.matchMedia?.('(min-width: 48rem)').matches === false) return;
    const first = rooms.find((r) => r.membership !== 'invite');
    if (first) onNavigate({ ...HOME, roomId: first.id });
  }, [loading, location.roomId, rooms, onNavigate]);

  // A fresh room resets whatever this pane's own local state was showing --
  // the same reason `ThreadPane`'s reply box keys itself off the thread id.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the room id is the trigger, not a value read here.
  useEffect(() => {
    setInfoOpen(false);
    setInfoTab('room');
  }, [location.roomId]);

  const selectRoom = useCallback(
    (roomId: string) => {
      setInfoOpen(false);
      setInfoTab('room');
      onNavigate({ ...HOME, roomId });
    },
    [onNavigate],
  );

  // The header pin (#255) always opens straight to the Pinned tab, even if
  // the panel is already open showing something else -- pressing it is a
  // request to see the pins, not a plain open/close toggle like the info
  // button beside it.
  const closeThreadRoute = useCallback(
    () => onNavigate({ roomId: location.roomId, threadId: null, eventId: null }),
    [onNavigate, location.roomId],
  );

  // The thread and the info panel share the third column, so opening one
  // closes the other.
  const openPinned = useCallback(() => {
    if (location.threadId) closeThreadRoute();
    setInfoTab('pinned');
    setInfoOpen(true);
  }, [location.threadId, closeThreadRoute]);

  const toggleInfo = useCallback(() => {
    if (location.threadId) {
      closeThreadRoute();
      setInfoOpen(true);
    } else {
      setInfoOpen((v) => !v);
    }
  }, [location.threadId, closeThreadRoute]);

  const openThread = useCallback(
    (rootId: string) => {
      setInfoOpen(false);
      onNavigate({ roomId: location.roomId, threadId: rootId, eventId: null });
    },
    [onNavigate, location.roomId],
  );

  const closeThread = useCallback(() => {
    setInfoOpen(false);
    closeThreadRoute();
  }, [closeThreadRoute]);

  const answerInvitation = useCallback(
    (roomId: string, accept: boolean) => {
      void (accept ? client.acceptInvitation(roomId) : client.declineInvitation(roomId));
    },
    [client],
  );

  const roomOpen = Boolean(location.roomId);
  const threadOpen = Boolean(location.threadId);
  const thirdOpen = threadOpen || (infoOpen && Boolean(selected));
  const classes = [
    'chat',
    'flex-1',
    'min-w-0',
    roomOpen && 'chat-room-open',
    thirdOpen && 'chat-third-open',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Panes className={classes}>
      <ChatList
        client={client}
        rooms={rooms}
        loading={loading}
        error={error}
        onRetry={retry}
        selectedRoomId={location.roomId}
        view={view}
        onViewChange={setView}
        {...(initialDialog ? { initialDialog } : {})}
        onSelectRoom={selectRoom}
        onAnswerInvitation={answerInvitation}
        encryptedIds={encryptedIds}
        {...(encryption ? { onOpenEncryption: () => setEncryptionOpen(true) } : {})}
      />
      {selected ? (
        <Room
          client={client}
          room={selected}
          onBack={() => onNavigate(HOME)}
          onOpenThread={openThread}
          infoOpen={infoOpen}
          onToggleInfo={toggleInfo}
          onOpenPinned={openPinned}
          calls={calls}
          {...(linkFor ? { linkFor } : {})}
          encrypted={encryptedIds.has(selected.id)}
        />
      ) : (
        <Panes.Column flex className="chat-room">
          <div className="grid flex-1 place-items-center p-8 chat-empty-pane">
            <Empty icon="chat">
              {error
                ? `Chat could not load. ${error.message}`
                : loading
                  ? 'Loading your rooms.'
                  : rooms.length === 0
                    ? 'You are not in any rooms yet.'
                    : 'Pick a room to read it.'}
            </Empty>
          </div>
        </Panes.Column>
      )}
      {selected && location.threadId && (
        <ThreadPanel
          client={client}
          roomId={selected.id}
          roomName={selected.isDirect ? selected.name : `#${selected.name}`}
          threadId={location.threadId}
          onClose={closeThread}
          {...(linkFor ? { linkFor } : {})}
        />
      )}
      {selected && !location.threadId && infoOpen && (
        <InfoPanel
          client={client}
          room={selected}
          onClose={() => setInfoOpen(false)}
          tab={infoTab}
          onTabChange={setInfoTab}
          hours={hours}
          onOpenThread={openThread}
        />
      )}
      {encryption && (encryptionOpen || incoming.request) && (
        <Encryption
          client={client}
          request={incoming.request}
          onRequestDone={incoming.dismiss}
          onClose={() => {
            setEncryptionOpen(false);
            incoming.dismiss();
          }}
        />
      )}
    </Panes>
  );
}
